import React, { useRef, useEffect, useState } from 'react';
import { MessageCircle, Phone, ArrowLeft, Users, Search, BookOpen, FileCheck } from 'lucide-react';
import { FaWhatsapp, FaWaze, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import ProcessSteps from '../components/ProcessSteps';
import ExpertiseAreas from '../components/ExpertiseAreas';
import ReasonsList from '../components/ReasonsCard';
// import ReviewsSlider from '../components/Reviews';
import LawReviewsWidget from '../components/LawReviewsWidget';
import ContactButton from '../components/ContactButton';
import PageMeta from '../components/PageMeta';
import { getPageMeta } from '../data/pageMetaConfig';
import { Link } from 'react-router-dom';
import EligibilityButton from '../components/EligibilityButton';
import AboutSection from '../components/AboutSection';
import ServicesSection from '../components/ServicesSection';
import { motion, useInView } from 'framer-motion';
import HomepageEligibilitySection from '../components/HomepageEligibilitySection';
import AllArticlesSlider from '../components/AllArticlesSlider';
import SuccessStoriesSlider from '../components/SuccessStoriesSlider';
import FAQAccordion from '../components/FAQAccordion';
import SimpleContactButton from '../components/SimpleCBT';
const OptimizedImage = ({ src, alt, className, style, isMobile, overlayColor = 'rgba(0,0,0,0)', overlayOpacity = 0 }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const placeholderColor = "#ffffff";

  useEffect(() => {
    const img = new Image();
    img.src = src;
    if (img.complete) setImageLoaded(true);
    else img.onload = () => setImageLoaded(true);
    return () => { img.onload = null; };
  }, [src]);

  return (
    <div className="relative w-full h-full overflow-hidden bg-white">
      <div
        className={`absolute inset-0 ${imageLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-700`}
        style={{ backgroundColor: placeholderColor }}
      />
      <img
        src={src}
        alt={alt}
        loading="eager"
        className={`${className} ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-700 w-full h-full object-cover`}
        style={{
          ...style,
          backgroundColor: '#ffffff',
          ...(isMobile && { minHeight: '100%' })
        }}
      />
      <div 
        className="absolute inset-0" 
        style={{ 
          backgroundColor: overlayColor,
          opacity: overlayOpacity
        }}
      ></div>
    </div>
  );
};

const ModernContactButton = ({ href, icon: Icon, label, external = false, type = "default" }) => {
  const getIconBackground = () => {
    switch (type) {
      case 'whatsapp':
        return "bg-gradient-to-r from-green-400 to-green-600";
      case 'email':
        return "bg-gradient-to-r from-amber-500 to-yellow-500";
      case 'waze':
        return "bg-gradient-to-r from-purple-400 to-purple-500";
      case 'phone':
        return "bg-gradient-to-r from-green-400 to-green-500";
      default:
        return "bg-gradient-to-r from-amber-500 to-yellow-500";
    }
  };

  return (
    <a 
      href={href}
      className={`
        w-12 h-12 md:w-16 md:h-16
        ${getIconBackground()} 
        rounded-2xl
        flex items-center justify-center
        group relative transform hover:scale-110 hover:-translate-y-2
        transition-all duration-500
        shadow-lg shadow-black/30 hover:shadow-xl hover:shadow-black/50
      `}
      title={label}
      target={external ? "_blank" : "_self"}
      rel={external ? "noopener noreferrer" : ""}
    >
      <Icon className="w-6 h-6 md:w-8 md:h-8 text-white group-hover:scale-110 transition-transform duration-300" />
    </a>
  );
};

const SectionWithImage = ({ 
  children, 
  imageSrc, 
  imagePosition = "left",
  imageAlt = ""
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  if (isMobile) {
    return <div>{children}</div>;
  }

  return (
    <div className="grid lg:grid-cols-5 min-h-screen">
      <div className={`relative lg:col-span-2 ${imagePosition === 'right' ? 'order-2' : 'order-1'}`}>
        <img 
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
      </div>

      <div className={`flex items-center justify-center bg-white lg:col-span-3 ${imagePosition === 'right' ? 'order-1' : 'order-2'}`}>
        <div className="w-full max-w-3xl px-8">
          {children}
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const expertiseRef = useRef(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    const imagePath = '/articlesImages/articleImage3.JPG';
    setBackgroundImage(imagePath);

    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = imagePath;
    document.head.appendChild(preloadLink);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
      if (preloadLink.parentNode) {
        preloadLink.remove();
      }
    };
  }, []);

  const renderHeroSection = () => {
    if (!backgroundImage) {
      return (
        <div className="relative h-screen bg-black flex items-center justify-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-amber-400/30 border-t-amber-400"></div>
            <div className="absolute inset-2 animate-pulse rounded-full h-16 w-16 border-4 border-transparent border-b-yellow-400/50"></div>
            <div className="absolute inset-4 animate-spin rounded-full h-12 w-12 border-2 border-transparent border-r-amber-300/60" style={{ animationDirection: 'reverse' }}></div>
          </div>
        </div>
      );
    }

  if (isMobile) {
  return (
   <div className="relative h-[80vh] overflow-hidden bg-black">
      <div className="relative w-full h-full">
        <OptimizedImage 
          src={backgroundImage}
          alt="עו״ד אראל"
          className="w-full h-full object-cover"
          style={{ 
            objectPosition: 'center center'
          }}
          isMobile={true}
          overlayColor="rgba(0, 0, 0, 0.75)"
          overlayOpacity={1}
        />
        
        <div className="absolute inset-0 px-4 py-4 flex flex-col justify-start">
          {/* שם המשרד למעלה - קטן יותר */}
          <div className="text-center pt-2">
            <h1 className="text-2xl font-bold text-white">
              עורך דין חדלות פירעון והוצאה לפועל
            </h1>
            <div className="flex items-center justify-center space-x-4 space-x-reverse mt-2">
              <div className="h-px bg-gradient-to-r from-white to-transparent flex-1 max-w-12"></div>
              <h2 className="text-lg font-light text-white">מיכלסון ושות</h2>
              <div className="h-px bg-gradient-to-l from-white to-transparent flex-1 max-w-12"></div>
            </div>
          </div>

          {/* משפט מפתח - גדול */}
          <div className="text-center mt-12">
            <h3 className="text-4xl font-bold text-white leading-tight px-4">
              הגיע הזמן לשים סוף לחובות ולצאת לדרך חדשה
            </h3>
          </div>

          {/* ריבוע */}
          <div className="mt-12">
            <div className="bg-black/50 backdrop-blur-sm rounded-xl px-6 py-4 border border-amber-400/30 mx-4">
              <p className="text-center leading-relaxed text-lg font-bold text-white">
                למשרדנו ניסיון רב ומוכח בניהול תיקים בתחום החובות על כל רבדיו, 
                ובעל הצלחות משפטיות רבות בתחום.
              </p>
            </div>
          </div>

          {/* כפתור עם רווח גדול יותר */}
      {/* כפתור עם רווח גדול יותר */}
<div className="flex justify-center px-4 mt-8">
  <Link 
    to="/contact" 
    className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white backdrop-blur-lg
               px-6 py-3 rounded-full font-bold shadow-lg shadow-black/30
               hover:bg-black/30 hover:shadow-xl
               transition-all duration-500 flex items-center gap-2
               transform hover:scale-105 hover:-translate-y-1
               border border-amber-400/30 hover:border-white/40
               text-sm"
  >
    <span>להשארת פרטים לחצו כאן</span>
  </Link>
</div>
        </div>
      </div>
    </div>
  );
}

    // Desktop & Tablet Design
    return (
      <div className="relative h-[75vh] overflow-hidden bg-black">
        <div className="absolute inset-0">
          <OptimizedImage 
            src={backgroundImage}
            alt="משרד עורכי דין"
            className="w-full h-full object-cover"
            style={{ 
              objectPosition: 'center center'
            }}
            isMobile={false}
            overlayColor="rgba(0, 0, 0, 0.65)"
            overlayOpacity={1}
          />
        </div>

        <div className="relative h-full flex items-center z-10">
          <div className="w-full max-w-7xl mx-auto px-8 lg:px-16">
            <div className="grid lg:grid-cols-3 gap-12 items-center">
              <div className="lg:col-span-2 space-y-12">
                {/* שם המשרד למעלה - קטן יותר */}
                <div className="space-y-3">
                  <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight">
                    עורך דין חדלות פירעון והוצאה לפועל
                  </h1>
                  <div className="flex items-center space-x-4 space-x-reverse">
                    <div className="h-px bg-gradient-to-r from-white to-transparent flex-1 max-w-32"></div>
                    <h2 className="text-xl xl:text-2xl font-light text-white">
                      מיכלסון ושות
                    </h2>
                    <div className="h-px bg-gradient-to-l from-white to-transparent flex-1 max-w-32"></div>
                  </div>
                </div>

                {/* משפט מפתח באמצע - גדול */}
                <div className="text-center lg:text-right">
                  <h3 className="text-4xl xl:text-5xl font-bold text-white leading-tight whitespace-nowrap">
                    הגיע הזמן לשים סוף לחובות ולצאת לדרך חדשה
                  </h3>
                </div>

                {/* ריבוע וכפתור למטה */}
           {/* ריבוע וכפתור למטה */}
<div className="space-y-6">
  <div className="bg-black/40 backdrop-blur-sm rounded-xl px-6 py-4 border border-amber-400/30">
    <p className="text-center leading-relaxed text-xl font-bold text-white">
      למשרדנו ניסיון רב ומוכח בניהול תיקים
      <br />
      בתחום החובות על כל רבדיו ובעל הצלחות משפטיות רבות בתחום.
    </p>
  </div>

  <div className="flex justify-center pt-6">
    <Link 
      to="/contact" 
      className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white backdrop-blur-lg
                 px-8 py-4 rounded-full font-bold shadow-lg shadow-black/30
                 hover:bg-black/30 hover:shadow-xl
                 transition-all duration-500 flex items-center gap-2
                 transform hover:scale-105 hover:-translate-y-1
                 border border-amber-400/30 hover:border-white/40"
    >
      <span>להשארת פרטים לחצו כאן</span>
    </Link>
  </div>
</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const homeMeta = getPageMeta('/');

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageMeta
        title={homeMeta.title}
        description={homeMeta.description}
        path="/"
      />
      {renderHeroSection()}
      
<div className="bg-white py-8 md:py-16">
  <div className="max-w-4xl mx-auto px-4 md:px-8">
    <div className="space-y-6 md:space-y-8">
      {/* שאלות */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight">
          מתמודדים עם עיקולים?
        </h2>
        <h2 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight">
          מרגישים שהחובות סוגרים עליכם?
        </h2>
        <h2 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight">
          הלחץ לא מרפה?
        </h2>
      </div>

      {/* הפרדה */}
      <div className="flex justify-center">
        <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full"></div>
      </div>

      {/* טקסט מרכזי */}
      <div className="text-center space-y-4">
        <p className="text-2xl md:text-3xl font-semibold text-gray-900 leading-relaxed">
          אנחנו מבינים את הפחד,
          <br />
          ואנחנו כאן כדי <span className="font-black text-gray-900">לשים לזה סוף!</span>
        </p>
        <p className="text-2xl md:text-3xl font-semibold text-gray-900 leading-relaxed max-w-3xl mx-auto">
          נלווה אתכם צעד צעד ויחד נבנה תכנית אמיתית
          <br />
          לשקט כלכלי ולחיים חדשים.
        </p>
      </div>
    </div>
  </div>
</div>
      
<HomepageEligibilitySection />
      <ServicesSection />
      <AboutSection />
      
      <div className="h-16 md:h-24"></div>
      <ReasonsList />
        <SuccessStoriesSlider />
  <SimpleContactButton />
   <ProcessSteps />
      <AllArticlesSlider />
     
    
      {/*
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <ReviewsSlider 
          showTitle={true}
          title="לקוחות ממליצים"
        />
      </div>
      */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <LawReviewsWidget />
      </div>
      <FAQAccordion />
      <ContactButton />
    </div>
  );
};

export default HomePage;